import groupBy from 'lodash.groupby';

import {
  RoomInfoViewModel,
  RoomVariationViewModel,
  RoomViewModel,
} from '@api/index';
import { FALLBACK_IMAGE_URL } from '@constants/constants';
import { optimizeImage } from '@utils/imageOptimization';

// =====================================================
// Get the lowest variation price in a rooms variations
// =====================================================
export const getLowestVariation = (roomsDetail: RoomInfoViewModel[]) => {
  const roomVariations: RoomVariationViewModel[] = [];

  if (!roomsDetail) return;

  roomsDetail?.forEach((room) =>
    room.informations?.forEach((roomInfo) =>
      roomVariations.push(...roomInfo.variations)
    )
  );

  const lowestPrice =
    Math.min(
      ...roomVariations.map((item) => item.totalDiscountedSellingPrice || 0)
    ) ?? 0;

  if (!lowestPrice) return roomVariations?.[0];

  const lowestPriceItem = roomVariations?.find(
    (item) => item.totalDiscountedSellingPrice === lowestPrice
  );

  return lowestPriceItem;
};

// =====================================================
// Group Images By Category
// =====================================================
export const groupImagesByCategory = <
  T extends {
    url?: string | null;
    hotelImageCategoryName?: string | null;
    placeholderUrl?: string | null;
    providerId?: number | null;
  }
>(
  images: T[],
  groupingKey?: string,
  hotelName?: string
) => {
  const imagesForGallery = images.map((p, i) => ({
    ...p,
    original:
      optimizeImage(p?.url, '1024', '768', {
        providerId: p?.providerId,
        placeholderUrl: p?.placeholderUrl,
      }) || FALLBACK_IMAGE_URL,
    originalAlt: `${hotelName || ''} ${p?.hotelImageCategoryName || ''} ${
      i + 1
    }`,
    thumbnailAlt: `${hotelName || ''} ${p.hotelImageCategoryName || ''} küçük ${
      i + 1
    }`,

    thumbnail:
      optimizeImage(p?.url || '', '360', '256', {
        providerId: p?.providerId,
        placeholderUrl: p?.placeholderUrl,
      }) || FALLBACK_IMAGE_URL,
    loading: 'lazy' as 'lazy' | 'eager',
    thumbnailLoading: 'lazy' as 'lazy' | 'eager',
  }));
  const imageCategories = groupingKey
    ? groupBy(imagesForGallery, groupingKey)
    : { 'Oda Fotografları': imagesForGallery };
  return {
    ...imageCategories,
    all: imagesForGallery,
  } as { [key: string]: typeof imagesForGallery };
};

export const getLowestRoomOption = (
  room: RoomViewModel,
  selectedTab: number
) => {
  const variations = room?.details?.flatMap((detail) =>
    detail.informations
      .filter((inf) => inf.requestedRoomIndex === selectedTab)
      .flatMap((information) =>
        information.variations.flatMap((variation) => variation)
      )
  );

  const lowestPrice = variations?.length
    ? Math.min(
        ...variations.map((item) => item.totalDiscountedSellingPrice || 0)
      )
    : 0;

  if (!lowestPrice) return variations?.[0];

  const lowestPriceItem = variations?.find(
    (item) => item.totalDiscountedSellingPrice === lowestPrice
  );

  return lowestPriceItem;
};
